import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { DashboardDateRangePicker } from '@/components/dashboard/DashboardDateRangePicker';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { adsApi } from '@/api/client';
import { auditTrailApi, type AuditTrailEvent } from '@/api/auditTrailApi';
import { toIsoDate } from '@/api/reportDateRanges';
import { cn } from '@/utils/cn';
import {
  AUDIT_SOURCE_OPTIONS,
  auditActorLabel,
  auditChangeLabel,
  auditEntityLabel,
  auditSourceBadgeVariant,
  auditSourceLabel,
  auditStatusLabel,
} from '@/pages/settings/auditTrailDisplay';

function defaultRange(now = new Date()): { startDate: string; endDate: string } {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
}

function profileOptions(data: unknown): { profileId: string; accountName: string }[] {
  if (!data || typeof data !== 'object') return [];
  const accounts = (data as { accounts?: unknown }).accounts;
  if (!Array.isArray(accounts)) return [];
  const out: { profileId: string; accountName: string }[] = [];
  for (const row of accounts) {
    if (!row || typeof row !== 'object') continue;
    const item = row as { profileId?: unknown; accountName?: unknown };
    const profileId = String(item.profileId ?? '').trim();
    if (!profileId) continue;
    const accountName = String(item.accountName ?? '').trim() || profileId;
    out.push({ profileId, accountName });
  }
  return out;
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function JsonBlock({ value }: { value: unknown }) {
  if (value == null) {
    return <p className="text-body-sm text-foreground-muted">None</p>;
  }
  return (
    <pre className="max-h-64 overflow-auto rounded-lg bg-page p-3 text-xs text-foreground">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function AuditTrailPanel() {
  const initialRange = useMemo(() => defaultRange(), []);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [profileId, setProfileId] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const profilesQuery = useQuery({
    queryKey: ['audit-trail-profiles'],
    queryFn: async () => {
      const res = await adsApi.getAdvertisingProfiles();
      if (!res.success) throw new Error(res.message || 'Failed to load accounts');
      return profileOptions(res.data);
    },
  });

  const listQuery = useQuery({
    queryKey: ['audit-trail', profileId, source, startDate, endDate, search, page],
    queryFn: async () => {
      const res = await auditTrailApi.list({
        profileId: profileId || undefined,
        source: source || undefined,
        from: startDate,
        to: endDate,
        q: search.trim() || undefined,
        page,
        pageSize: 50,
      });
      if (!res.success) throw new Error(res.message || 'Failed to load audit trail');
      return res.data;
    },
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageSize = listQuery.data?.pageSize ?? 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
        <DashboardDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(range) => {
            setStartDate(range.startDate);
            setEndDate(range.endDate);
            setPage(1);
          }}
          variant="default"
          showHelp={false}
        />
        <label className="min-w-[12rem]">
          <span className="mb-1 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
            Account
          </span>
          <Select
            value={profileId}
            onChange={(e) => {
              setProfileId(e.target.value);
              setPage(1);
            }}
            className="h-11 w-full"
          >
            <option value="">All accounts</option>
            {(profilesQuery.data ?? []).map((row) => (
              <option key={row.profileId} value={row.profileId}>
                {row.accountName}
              </option>
            ))}
          </Select>
        </label>
        <label className="min-w-[11rem]">
          <span className="mb-1 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
            Source
          </span>
          <Select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(1);
            }}
            className="h-11 w-full"
          >
            <option value="">All sources</option>
            {AUDIT_SOURCE_OPTIONS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="min-w-[14rem] flex-1">
          <span className="mb-1 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
            Search
          </span>
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Entity, summary, user…"
            className="h-11"
          />
        </label>
      </div>

      {listQuery.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : listQuery.isError ? (
        <p className="rounded-xl border border-error/20 bg-error-subtle px-4 py-3 text-body-sm text-error-ink">
          Could not load the audit trail. Try again in a moment.
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-border bg-surface px-4 py-8 text-center text-body-sm text-foreground-muted">
          Changes to Amazon campaigns, ads, and budgets will appear here.
        </p>
      ) : (
        <>
          <TableWrap className="rounded-xl border border-border bg-surface">
            <Table compact>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>Time</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <tbody>
                {items.map((event) => (
                  <AuditTrailRow
                    key={event.id}
                    event={event}
                    expanded={expandedId === event.id}
                    onToggle={() => setExpandedId((cur) => (cur === event.id ? null : event.id))}
                  />
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <div className="flex items-center justify-between gap-3 text-body-sm text-foreground-muted">
            <span>
              {total} change{total === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                className="h-9 px-3"
                disabled={page <= 1}
                onClick={() => setPage((cur) => Math.max(1, cur - 1))}
              >
                Previous
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                className="h-9 px-3"
                disabled={page >= totalPages}
                onClick={() => setPage((cur) => cur + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AuditTrailRow({
  event,
  expanded,
  onToggle,
}: {
  event: AuditTrailEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  const failed = event.status === 'failed' || event.status === 'error';
  return (
    <Fragment>
      <TableRow className={cn(expanded && 'bg-surface-muted/60')}>
        <TableCell compact>
          <button
            type="button"
            onClick={onToggle}
            className="rounded-lg p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide details' : 'Show details'}
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
          </button>
        </TableCell>
        <TableCell compact className="whitespace-nowrap text-body-sm">
          {formatWhen(event.createdAt)}
        </TableCell>
        <TableCell compact className="max-w-[10rem] truncate text-body-sm">
          {event.accountName || event.profileId}
        </TableCell>
        <TableCell compact>
          <Badge variant={auditSourceBadgeVariant(event.source)}>
            {auditSourceLabel(event.source)}
          </Badge>
        </TableCell>
        <TableCell compact className="max-w-[12rem] truncate text-body-sm">
          {auditActorLabel(event)}
        </TableCell>
        <TableCell compact className="max-w-[14rem] truncate text-body-sm">
          {auditEntityLabel(event)}
        </TableCell>
        <TableCell compact className="max-w-[16rem] truncate text-body-sm">
          {auditChangeLabel(event)}
        </TableCell>
        <TableCell compact>
          <Badge variant={failed ? 'danger' : 'success'}>{auditStatusLabel(event.status)}</Badge>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow>
          <TableCell colSpan={8} className="bg-page">
            <div className="grid gap-4 p-2 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <p className="mb-1 text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
                  Before
                </p>
                <JsonBlock value={event.before} />
              </div>
              <div>
                <p className="mb-1 text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
                  After
                </p>
                <JsonBlock value={event.after} />
              </div>
              <div>
                <p className="mb-1 text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
                  Amazon request
                </p>
                <JsonBlock value={event.amazonRequest} />
              </div>
              <div>
                <p className="mb-1 text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
                  Amazon response
                </p>
                <JsonBlock value={event.amazonResponse} />
                {event.errorMessage ? (
                  <p className="mt-2 text-body-sm text-error-ink">{event.errorMessage}</p>
                ) : null}
              </div>
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
}
